import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, CheckCircle2, DollarSign, FileScan, Loader2, Play, Truck, XCircle,
} from "lucide-react";

/** PETERBILT SCAN — the price first, the trucks next, his yes before anything is stored.
 *
 *  A photocopy carries no text, so it has to be read by OCR, and OCR costs money. So this panel
 *  never starts on its own: it prices the file for free, then reads ONLY the VIN in each page
 *  header, then shows him every truck it found — VIN, which pages, whether the check digit adds up,
 *  and whether that truck is in his yard — and waits. Nothing touches a truck until he ticks it. */

export function PeterbiltScanPanel({ canManage = false, testid = "peterbilt-scan" }) {
  const [file, setFile] = useState(null);
  const [est, setEst] = useState(null);
  const [split, setSplit] = useState(null);
  const [ticked, setTicked] = useState({});
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const poll = useRef(null);

  const watch = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/sheet-readers/peterbilt/${id}`);
      setSplit(data);
      if (data.status === "done") {
        const pre = {};
        (data.trucks || []).forEach((t) => { pre[t.vin] = Boolean(t.in_the_yard && t.check_digit_ok); });
        setTicked(pre);
      }
      if (data.status === "done" || data.status === "failed") {
        clearInterval(poll.current);
        poll.current = null;
      }
    } catch { /* keep polling */ }
  }, []);

  useEffect(() => {
    // a split that is already running (or finished) can be opened straight from a link
    const id = new URLSearchParams(window.location.search).get("peterbilt_split");
    if (id) {
      setSplit({ status: "running", id });
      watch(id);
      poll.current = setInterval(() => watch(id), 4000);
    }
    return () => { if (poll.current) clearInterval(poll.current); };
  }, [watch]);

  const priceIt = async (f) => {
    if (!f) return;
    setBusy("price"); setEst(null); setSplit(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/sheet-readers/peterbilt/estimate", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setEst(data); setFile(f);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That file could not be priced.",
        { duration: 12000 });
    } finally { setBusy(""); }
  };

  const readHeaders = async () => {
    setBusy("split");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirm_cost", "true");
      const { data } = await api.post("/sheet-readers/peterbilt/split", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const id = data.split_id || data.id;
      setSplit({ status: "running", pages: data.pages, id });
      poll.current = setInterval(() => watch(id), 4000);
      watch(id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "It could not start.", { duration: 12000 });
    } finally { setBusy(""); }
  };

  const attach = async () => {
    const vins = Object.keys(ticked).filter((v) => ticked[v]);
    if (!vins.length) { toast.error("Tick the trucks you want first."); return; }
    setBusy("attach");
    try {
      const { data } = await api.post(`/sheet-readers/peterbilt/${split.id}/attach`, { vins });
      setResult(data);
      toast.success(data.say || "Attached.", { duration: 12000 });
      watch(split.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nothing was attached.", { duration: 14000 });
    } finally { setBusy(""); }
  };

  const tickedCount = Object.values(ticked).filter(Boolean).length;

  return (
    <section data-testid={testid} className="rounded-xl border-2 border-[#1F1F1F] bg-white overflow-hidden">
      <div className="bg-[#1F1F1F] text-white px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileScan className="w-5 h-5 shrink-0" />
          <h2 className="font-display text-lg font-bold truncate">Peterbilt summaries — one scan, many trucks</h2>
        </div>
        <span data-testid={`${testid}-state`}
          className="text-[10px] font-mono uppercase tracking-widest bg-amber-500 text-black px-2 py-1 rounded shrink-0">
          Costs money
        </span>
      </div>

      <div className="p-4 space-y-4">
        <p className="text-sm text-slate-700">
          A photocopy has no text in it, so it has to be <b>read by eye (OCR)</b>, and that costs a
          little. It prices the file first for nothing, then reads <b>only the VIN in each page header</b>,
          then shows you every truck it found. <b>Nothing is stored against any truck until you tick it.</b>
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden"
            data-testid={`${testid}-file`}
            onChange={(e) => priceIt(e.target.files?.[0])} />
          <Button data-testid={`${testid}-pick`} variant="outline" disabled={!canManage || busy === "price"}
            onClick={() => fileRef.current?.click()} className="rounded-lg h-12 px-4 border-slate-300 font-bold">
            {busy === "price" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <DollarSign className="w-4 h-4 mr-1.5" />}
            Price a scan first
          </Button>
          {!canManage && (
            <span data-testid={`${testid}-locked`} className="text-sm text-slate-500">
              Only someone who can change trucks can run this.
            </span>
          )}
        </div>

        {est && (
          <div data-testid={`${testid}-estimate`} className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 space-y-2">
            <p className="text-sm text-slate-800" data-testid={`${testid}-say`}>{est.say}</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-white border border-amber-200 p-2">
                <div className="font-display text-2xl font-black">{est.pages}</div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500">pages</div>
              </div>
              <div className="rounded-lg bg-white border border-amber-200 p-2">
                <div className="font-display text-2xl font-black" data-testid={`${testid}-split-cost`}>
                  ${est.estimate?.split_usd}
                </div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500">to find the trucks</div>
              </div>
              <div className="rounded-lg bg-white border border-amber-200 p-2">
                <div className="font-display text-2xl font-black">${est.estimate?.total_usd}</div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500">if fully read</div>
              </div>
            </div>
            <p className="text-[11px] text-slate-600">
              <b>An estimate, not a bill</b> — {est.estimate?.basis}. Nothing is spent until you press.
            </p>
            <Button data-testid={`${testid}-go`} onClick={readHeaders}
              disabled={!canManage || busy === "split" || split?.status === "running"}
              className="rounded-lg h-12 px-4 bg-[#B01E28] hover:bg-[#8f1820] font-bold w-full sm:w-auto">
              {busy === "split" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
              Read the VIN headers — about ${est.estimate?.split_usd}
            </Button>
          </div>
        )}

        {split?.status === "running" && (
          <div data-testid={`${testid}-running`} className="rounded-lg border-2 border-blue-200 bg-blue-50 p-3 flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-blue-700 shrink-0" />
            <span className="text-sm text-slate-800">
              Looking at page {split.pages_read || 0} of {split.pages}. Nothing stored yet — you will get the list.
            </span>
          </div>
        )}

        {split?.status === "failed" && (
          <div data-testid={`${testid}-failed`} className="rounded-lg border-2 border-red-300 bg-red-50 p-3 text-sm text-slate-800">
            <b>It stopped:</b> {split.error || "the read did not finish"}. Nothing was stored against any truck.
          </div>
        )}

        {split?.status === "done" && (
          <div data-testid={`${testid}-trucks`} className="space-y-3">
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3">
              <p className="text-sm text-slate-800" data-testid={`${testid}-found`}>
                <b>{(split.trucks || []).length} trucks</b> in {split.pages} pages
                ({split.headers} headers — a summary that runs over re-prints its header).
                <b> {split.in_the_yard}</b> of them are in your yard.
                Spent: <b>${split.spent_usd}</b>.
              </p>
              {!!(split.failed_check_digit || []).length && (
                <p className="text-sm text-amber-900 mt-1" data-testid={`${testid}-bad-digits`}>
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  {split.failed_check_digit.length} VIN(s) do not add up — left for a person, never corrected here.
                </p>
              )}
              {!!(split.unplaced_pages || []).length && (
                <p className="text-sm text-slate-700 mt-1" data-testid={`${testid}-unplaced`}>
                  Page(s) {split.unplaced_pages.join(", ")} came before any header — listed, not guessed at.
                </p>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              {(split.trucks || []).map((t) => (
                <label key={t.vin} data-testid={`${testid}-row-${t.vin}`}
                  className="flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" className="mt-1 w-5 h-5 accent-[#B01E28]"
                    data-testid={`${testid}-tick-${t.vin}`}
                    checked={Boolean(ticked[t.vin])}
                    disabled={!t.in_the_yard || !t.check_digit_ok}
                    onChange={(e) => setTicked((p) => ({ ...p, [t.vin]: e.target.checked }))} />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm font-bold text-[#1F1F1F] break-all">{t.vin}</div>
                    <div className="text-[12px] text-slate-600">
                      page{t.pages.length > 1 ? "s" : ""} {t.pages.join(", ")}
                      {t.headers > 1 && " · header printed twice"}
                      {" · "}
                      {t.check_digit_ok
                        ? <span className="text-emerald-700">check digit adds up</span>
                        : <span className="text-[#B01E28] font-bold">check digit FAILS</span>}
                    </div>
                    <div className="text-[12px] mt-0.5">
                      {t.in_the_yard
                        ? <span className="text-emerald-800 font-bold">
                            <Truck className="w-3.5 h-3.5 inline mr-1" />{t.stock_number}
                          </span>
                        : <span className="text-slate-500">not in your yard — nothing is created for it</span>}
                      {t.already_has_sheet && <span className="text-amber-800 ml-2">already has a build sheet</span>}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <Button data-testid={`${testid}-attach`} onClick={attach}
              disabled={!canManage || !tickedCount || busy === "attach"}
              className="rounded-lg h-12 px-4 bg-[#1F1F1F] hover:bg-black font-bold w-full sm:w-auto">
              {busy === "attach" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              Yes — put these {tickedCount} truck{tickedCount === 1 ? "'s" : "s'"} pages on their trucks
            </Button>
          </div>
        )}

        {result && (
          <div data-testid={`${testid}-result`} className="rounded-lg border-2 border-[#1F1F1F] bg-slate-50 p-3 space-y-2">
            <p className="text-sm text-slate-800"><b>{(result.attached || []).length} attached.</b> {result.say}</p>
            {(result.attached || []).map((a) => (
              <div key={a.vin} data-testid={`${testid}-attached-${a.vin}`} className="text-[12px] text-slate-700">
                <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 text-emerald-700" />
                <span className="font-mono">{a.vin}</span> → {a.stock_number} · pages {a.pages.join(", ")}
              </div>
            ))}
            {(result.skipped || []).map((sk) => (
              <div key={sk.vin} data-testid={`${testid}-skipped-${sk.vin}`} className="text-[12px] text-slate-700">
                <XCircle className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                <span className="font-mono">{sk.vin}</span> — {sk.why}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default PeterbiltScanPanel;
